// ===========================================
// SERVICE: Tareas (CORE)
// ===========================================

import type { SupabaseClient } from '@supabase/supabase-js'
import type { Database, Tarea, TareaCompleta, TareaComentario, EstadoTarea } from '@/types/database'
import { fetchInChunks } from '@/lib/postgrest-chunks'
import { ErrorParaElUsuario } from '@/lib/permisos-guard'
import { hoyEnArgentina } from './tareasRecurrentesService'
import type {
  FiltrosTareas,
  TareaFormData,
  TipoVinculado,
  TareaSubtarea,
  SubtareaFormData,
  TareaRecordatorio,
  RecordatorioFormData,
} from '../types/tareas'

/** Instante en que empieza en Argentina el día `fecha`, en formato ISO con su offset. Argentina no tiene horario de verano. */
export const inicioDiaArgentina = (fecha: string): string => `${fecha}T00:00:00-03:00`

/** Día calendario siguiente a `fecha`, las dos en formato ISO corto. */
export const diaSiguiente = (fecha: string): string => {
  const dia = new Date(`${fecha}T00:00:00Z`)
  dia.setUTCDate(dia.getUTCDate() + 1)
  return dia.toISOString().slice(0, 10)
}

/** Lo que ve el usuario si borra una tarea mientras el cron genera su ocurrencia. */
export const TEXTO_TAREAS_EN_GENERACION =
  'Se están generando las tareas de las repeticiones. Volvé a intentarlo en unos segundos.'

// ===========================================
// Tipos de respuesta
// ===========================================

export interface EstadisticasTareas {
  pendientes: number
  enProceso: number
  completadas: number
  vencidasHoy: number
  urgentes: number
}

// ===========================================
// Service
// ===========================================

/**
 * Servicio para gestión de tareas.
 *
 * IMPORTANTE: Todos los métodos reciben el cliente Supabase autenticado como primer parámetro.
 * Usar con useSupabase() desde el hook que llame a estos métodos.
 *
 * @example
 * const { supabase } = useSupabase()
 * const tareas = await tareasService.listar(supabase!, filtros)
 */
export const tareasService = {
  /**
   * Obtener todas las tareas con filtros
   * Soporta filtrado por workspace y vinculación polimórfica
   */
  async listar(client: SupabaseClient<Database>, filtros?: FiltrosTareas): Promise<TareaCompleta[]> {
    let query = client
      .from('vista_tareas_completas')
      .select('*')
      .order('posicion')
      .order('created_at', { ascending: false })

    // Filtros básicos
    if (filtros?.estado) {
      query = query.eq('estado', filtros.estado)
    }
    if (filtros?.asignado_a) {
      query = query.eq('asignado_a', filtros.asignado_a)
    }
    if (filtros?.asignado_por) {
      query = query.eq('asignado_por', filtros.asignado_por)
    }
    // "Míos" es la tarea asignada a mí, sin mirar quién la creó.
    if (filtros?.propias_de) {
      query = query.eq('asignado_a', filtros.propias_de)
    }
    if (filtros?.cliente_id) {
      query = query.eq('cliente_id', filtros.cliente_id)
    }
    if (filtros?.prioridad) {
      query = query.eq('prioridad', filtros.prioridad)
    }
    if (filtros?.busqueda) {
      query = query.ilike('titulo', `%${filtros.busqueda}%`)
    }

    // Filtro por workspace
    if (filtros?.workspace) {
      query = query.eq('workspace', filtros.workspace)
    }

    // Filtros de vinculación polimórfica
    if (filtros?.vinculado_tipo) {
      query = query.eq('vinculado_tipo', filtros.vinculado_tipo)
    }
    if (filtros?.vinculado_id) {
      query = query.eq('vinculado_id', filtros.vinculado_id)
    }

    // Filtro de completadas con ventana temporal (server-side)
    if (filtros?.mostrarCompletadas && filtros.completadasDesde) {
      // Traer no-completadas + completadas dentro de la ventana temporal
      // Desde y hasta son días de Argentina, y el hasta incluye su día entero.
      const desde = `fecha_completada.gte."${inicioDiaArgentina(filtros.completadasDesde)}"`
      const hasta = filtros.completadasHasta
        ? `,fecha_completada.lt."${inicioDiaArgentina(diaSiguiente(filtros.completadasHasta))}"`
        : ''
      const completadasFilter = `estado.neq.completada,and(estado.eq.completada,${desde}${hasta})`
      query = query.or(completadasFilter)
    } else if (!filtros?.mostrarCompletadas) {
      query = query.neq('estado', 'completada')
    }
    // Si mostrarCompletadas=true pero sin completadasDesde: traer todas (no aplicar filtro)

    const { data, error } = await query

    if (error) throw error
    return data as TareaCompleta[]
  },

  /**
   * Obtener tarea por ID
   */
  async obtenerPorId(client: SupabaseClient<Database>, id: string): Promise<TareaCompleta> {
    const { data, error } = await client
      .from('vista_tareas_completas')
      .select('*')
      .eq('id', id)
      .single()

    if (error) throw error
    return data as TareaCompleta
  },

  /**
   * Crear nueva tarea con soporte de workspace y vinculación
   */
  async crear(client: SupabaseClient<Database>, tarea: TareaFormData & { created_by: string; organizacion_id: string }): Promise<Tarea> {
    // `posicion` no viaja: el trigger `trg_tareas_posicion_al_final` pone la tarea al final de su organización.
    const insertData = {
      titulo: tarea.titulo,
      descripcion: tarea.descripcion || null,
      cliente_id: tarea.cliente_id || null,
      organizacion_id: tarea.organizacion_id,
      vencimiento_id: tarea.vencimiento_id || null,
      asignado_a: tarea.asignado_a || null,
      asignado_por: tarea.asignado_a ? tarea.created_by : null,
      prioridad: tarea.prioridad,
      fecha_limite: tarea.fecha_limite || null,
      created_by: tarea.created_by,
      estado: 'pendiente' as const,
      // Nuevos campos de workspace y vinculación
      workspace: tarea.workspace || 'contable',
      vinculado_tipo: tarea.vinculado_tipo || null,
      vinculado_id: tarea.vinculado_id || null,
      tipo: tarea.tipo ?? null,
      cuit: tarea.cuit ?? null,
    }

    const { data, error } = await client
      .from('tareas')
      .insert(insertData)
      .select()
      .single()

    if (error) throw error
    return data as Tarea
  },

  /**
   * Actualizar tarea
   */
  async actualizar(client: SupabaseClient<Database>, id: string, datos: Partial<TareaFormData>): Promise<Tarea> {
    // Only include fields that are explicitly provided (not undefined)
    // This prevents partial updates from erasing unrelated fields
    const updateData: Record<string, unknown> = { updated_at: new Date().toISOString() }
    if (datos.titulo !== undefined) updateData.titulo = datos.titulo
    if (datos.descripcion !== undefined) updateData.descripcion = datos.descripcion ?? null
    if (datos.asignado_a !== undefined) updateData.asignado_a = datos.asignado_a ?? null
    if (datos.prioridad !== undefined) updateData.prioridad = datos.prioridad
    if (datos.fecha_limite !== undefined) updateData.fecha_limite = datos.fecha_limite ?? null
    if (datos.workspace !== undefined) updateData.workspace = datos.workspace
    if (datos.vinculado_tipo !== undefined) updateData.vinculado_tipo = datos.vinculado_tipo ?? null
    if (datos.vinculado_id !== undefined) updateData.vinculado_id = datos.vinculado_id ?? null

    const { data, error } = await client
      .from('tareas')
      .update(updateData)
      .eq('id', id)
      .select()
      .single()

    if (error) throw error
    return data as Tarea
  },

  /**
   * Cambiar estado de tarea (usa función SECURITY DEFINER)
   */
  async cambiarEstado(client: SupabaseClient<Database>, id: string, nuevoEstado: EstadoTarea): Promise<Tarea> {
    const { data, error } = await client.rpc('cambiar_estado_tarea', {
      p_tarea_id: id,
      p_nuevo_estado: nuevoEstado,
    })

    if (error) throw error
    return data as Tarea
  },

  /**
   * Guarda el orden de una columna del tablero. Cada id de `orden` queda con posicion igual a su índice.
   * El permiso se chequea sobre `tareaId`, la tarea arrastrada.
   */
  async reordenar(client: SupabaseClient<Database>, tareaId: string, orden: string[]): Promise<void> {
    const { error } = await client.rpc('reordenar_tareas' as never, {
      p_tarea_id: tareaId,
      p_orden: orden,
    } as never)

    if (error) throw error
  },

  /**
   * Eliminar tarea. Mientras el cron genera la ocurrencia de una tarea de una repetición, la base
   * rechaza el borrado con 55P03; el error dice que hay que reintentar.
   */
  async eliminar(client: SupabaseClient<Database>, id: string): Promise<void> {
    // RPC con RBAC: verifica rol del caller antes de eliminar
    const { error } = await client.rpc('eliminar_tarea_segura', { p_id: id })
    if (!error) return
    if ((error as { code?: string }).code === '55P03') throw new ErrorParaElUsuario(TEXTO_TAREAS_EN_GENERACION, error)
    throw error
  },

  /**
   * Obtener tareas de un usuario específico
   */
  async obtenerMisTareas(client: SupabaseClient<Database>, email: string, workspace?: string): Promise<TareaCompleta[]> {
    let query = client
      .from('vista_tareas_completas')
      .select('*')
      .eq('asignado_a', email)
      .neq('estado', 'completada')
      .order('prioridad')
      .order('fecha_limite')

    if (workspace) {
      query = query.eq('workspace', workspace)
    }

    const { data, error } = await query

    if (error) throw error
    return data as TareaCompleta[]
  },

  /**
   * Obtener tareas de un cliente específico
   */
  async obtenerPorCliente(client: SupabaseClient<Database>, clienteId: string): Promise<TareaCompleta[]> {
    const { data, error } = await client
      .from('vista_tareas_completas')
      .select('*')
      .eq('cliente_id', clienteId)
      .order('created_at', { ascending: false })

    if (error) throw error
    return data as TareaCompleta[]
  },

  /**
   * Obtener tareas vinculadas a una entidad (vinculación polimórfica)
   */
  async obtenerPorEntidad(
    client: SupabaseClient<Database>,
    entidadTipo: TipoVinculado,
    entidadId: string
  ): Promise<TareaCompleta[]> {
    const { data, error } = await client
      .from('vista_tareas_completas')
      .select('*')
      .eq('vinculado_tipo', entidadTipo)
      .eq('vinculado_id', entidadId)
      .order('created_at', { ascending: false })

    if (error) throw error
    return data as TareaCompleta[]
  },

  /**
   * Vincular tarea a una entidad
   */
  async vincular(
    client: SupabaseClient<Database>,
    tareaId: string,
    entidadTipo: TipoVinculado,
    entidadId: string
  ): Promise<Tarea> {
    const { data, error } = await client
      .from('tareas')
      .update({
        vinculado_tipo: entidadTipo,
        vinculado_id: entidadId,
        updated_at: new Date().toISOString(),
      })
      .eq('id', tareaId)
      .select()
      .single()

    if (error) throw error
    return data as Tarea
  },

  /**
   * Desvincular tarea de una entidad
   */
  async desvincular(client: SupabaseClient<Database>, tareaId: string): Promise<Tarea> {
    const { data, error } = await client
      .from('tareas')
      .update({
        vinculado_tipo: null,
        vinculado_id: null,
        updated_at: new Date().toISOString(),
      })
      .eq('id', tareaId)
      .select()
      .single()

    if (error) throw error
    return data as Tarea
  },

  // ==========================================
  // COMENTARIOS
  // ==========================================

  /**
   * Último comentario de cada tarea, para mostrarlo en el listado (ORR-259).
   *
   * Trae los comentarios de las tareas pedidas ordenados de más nuevo a más viejo y
   * se queda con el primero de cada una. Ordenar en Postgres y descartar acá evita
   * una query por tarea; el volumen lo permite (12 comentarios sobre 105 tareas en
   * PROD al 17/08/2026).
   *
   * NO usar un `.limit()` global sin filtrar por tarea: cortaría la lista antes de
   * llegar a las tareas del fondo y esas mostrarían "sin comentarios" siendo falso.
   * Es el bug de ORR-237.
   */
  async ultimosComentarios(
    client: SupabaseClient<Database>,
    tareaIds: string[]
  ): Promise<Record<string, TareaComentario>> {
    const comentarios = await fetchInChunks(tareaIds, async (batch) => {
      const { data, error } = await client
        .from('tarea_comentarios')
        .select('*')
        .in('tarea_id', batch)
        .order('created_at', { ascending: false })

      if (error) throw error
      return (data ?? []) as TareaComentario[]
    })

    return comentarios.reduce<Record<string, TareaComentario>>((acc, comentario) => {
      if (!acc[comentario.tarea_id]) acc[comentario.tarea_id] = comentario
      return acc
    }, {})
  },

  /**
   * Obtener comentarios de una tarea
   */
  async obtenerComentarios(client: SupabaseClient<Database>, tareaId: string): Promise<TareaComentario[]> {
    const { data, error } = await client
      .from('tarea_comentarios')
      .select('*')
      .eq('tarea_id', tareaId)
      .order('created_at')

    if (error) throw error
    return data as TareaComentario[]
  },

  /**
   * Agregar comentario a una tarea
   */
  async agregarComentario(
    client: SupabaseClient<Database>,
    tareaId: string,
    contenido: string,
    autor: string
  ): Promise<TareaComentario> {
    const { data, error } = await client
      .from('tarea_comentarios')
      .insert({
        tarea_id: tareaId,
        contenido,
        autor,
      })
      .select()
      .single()

    if (error) throw error
    return data as TareaComentario
  },

  /**
   * Eliminar comentario
   */
  async eliminarComentario(client: SupabaseClient<Database>, id: string): Promise<void> {
    const { error } = await client
      .from('tarea_comentarios')
      .delete()
      .eq('id', id)

    if (error) throw error
  },

  // ==========================================
  // SUBTAREAS (CHECKLIST)
  // ==========================================

  /**
   * Obtener subtareas de una tarea
   */
  async obtenerSubtareas(client: SupabaseClient<Database>, tareaId: string): Promise<TareaSubtarea[]> {
    const { data, error } = await client
      .from('tarea_subtareas')
      .select('*')
      .eq('tarea_id', tareaId)
      .order('posicion')

    if (error) throw error
    return data as TareaSubtarea[]
  },

  /**
   * Crear subtarea
   */
  async crearSubtarea(
    client: SupabaseClient<Database>,
    tareaId: string,
    datos: SubtareaFormData
  ): Promise<TareaSubtarea> {
    // Obtener organizacion_id a través del cliente de la tarea
    const { data: tarea, error: tareaError } = await client
      .from('tareas')
      .select('cliente_id, clientes(organizacion_id)')
      .eq('id', tareaId)
      .single()

    if (tareaError || !tarea) throw tareaError || new Error('Tarea no encontrada')

    // organizacion_id puede ser null si la tarea no tiene cliente
    const organizacionId = (tarea.clientes as { organizacion_id: string } | null)?.organizacion_id || null

    // Obtener la máxima posición actual
    const { data: maxPosData } = await client
      .from('tarea_subtareas')
      .select('posicion')
      .eq('tarea_id', tareaId)
      .order('posicion', { ascending: false })
      .limit(1)

    const nuevaPosicion = maxPosData && maxPosData.length > 0
      ? (maxPosData[0].posicion || 0) + 1
      : 0

    const { data, error } = await client
      .from('tarea_subtareas')
      .insert({
        tarea_id: tareaId,
        organizacion_id: organizacionId,
        titulo: datos.titulo,
        completada: datos.completada ?? false,
        posicion: datos.posicion ?? nuevaPosicion,
      })
      .select()
      .single()

    if (error) throw error
    return data as TareaSubtarea
  },

  /**
   * Crea las subtareas de una tarea recién creada en un solo insert, sin completar y en el orden de
   * `titulos`. Con la lista vacía no escribe nada.
   */
  async crearSubtareasEnLote(
    client: SupabaseClient<Database>,
    tareaId: string,
    organizacionId: string,
    titulos: string[]
  ): Promise<void> {
    if (titulos.length === 0) return
    const { error } = await client.from('tarea_subtareas').insert(
      titulos.map((titulo, posicion) => ({
        tarea_id: tareaId,
        organizacion_id: organizacionId,
        titulo,
        completada: false,
        posicion,
      }))
    )
    if (error) throw error
  },

  /**
   * Vincula una inscripción con el cliente que se dio de alta a partir de ella. Solo escribe si la
   * tarea es una inscripción sin cliente. Devuelve false si no escribió: la tarea ya tenía cliente,
   * no es una inscripción o la RLS no deja editarla.
   */
  async vincularClienteDeInscripcion(
    client: SupabaseClient<Database>,
    tareaId: string,
    clienteId: string
  ): Promise<boolean> {
    const { data, error } = await client
      .from('tareas')
      .update({ cliente_id: clienteId, updated_at: new Date().toISOString() })
      .eq('id', tareaId)
      .eq('tipo', 'inscripcion')
      .is('cliente_id', null)
      .select('id')

    if (error) throw error
    return (data?.length ?? 0) > 0
  },

  /**
   * Actualizar subtarea
   */
  async actualizarSubtarea(
    client: SupabaseClient<Database>,
    id: string,
    datos: Partial<SubtareaFormData>
  ): Promise<TareaSubtarea> {
    const { data, error } = await client
      .from('tarea_subtareas')
      .update({
        ...datos,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single()

    if (error) throw error
    return data as TareaSubtarea
  },

  /**
   * Toggle completada de subtarea
   */
  async toggleSubtarea(client: SupabaseClient<Database>, id: string, completada: boolean): Promise<TareaSubtarea> {
    const { data, error } = await client
      .from('tarea_subtareas')
      .update({
        completada,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single()

    if (error) throw error
    return data as TareaSubtarea
  },

  /**
   * Eliminar subtarea
   */
  async eliminarSubtarea(client: SupabaseClient<Database>, id: string): Promise<void> {
    const { error } = await client
      .from('tarea_subtareas')
      .delete()
      .eq('id', id)

    if (error) throw error
  },

  /**
   * Reordenar subtareas
   */
  async reordenarSubtareas(
    client: SupabaseClient<Database>,
    subtareas: { id: string; posicion: number }[]
  ): Promise<void> {
    // Actualizar posiciones en batch
    for (const { id, posicion } of subtareas) {
      const { error } = await client
        .from('tarea_subtareas')
        .update({ posicion, updated_at: new Date().toISOString() })
        .eq('id', id)

      if (error) throw error
    }
  },

  // ==========================================
  // ESTADÍSTICAS
  // ==========================================

  /**
   * Obtener estadísticas de tareas (opcionalmente por workspace)
   */
  async obtenerEstadisticas(client: SupabaseClient<Database>, workspace?: string): Promise<EstadisticasTareas> {
    let query = client
      .from('tareas')
      .select('estado, prioridad, fecha_limite, workspace')

    if (workspace) {
      query = query.eq('workspace', workspace)
    }

    const { data, error } = await query

    if (error) throw error

    const hoy = hoyEnArgentina()
    const tareas = data ?? []

    return {
      pendientes: tareas.filter(t => t.estado === 'pendiente').length,
      enProceso: tareas.filter(t => t.estado === 'en_proceso').length,
      completadas: tareas.filter(t => t.estado === 'completada').length,
      vencidasHoy: tareas.filter(t =>
        t.estado !== 'completada' &&
        t.fecha_limite &&
        t.fecha_limite <= hoy
      ).length,
      urgentes: tareas.filter(t =>
        t.estado !== 'completada' &&
        t.prioridad === 'urgente'
      ).length,
    }
  },

  // ==========================================
  // RECORDATORIOS
  // ==========================================

  /**
   * Obtener recordatorios de una tarea
   */
  async obtenerRecordatorios(client: SupabaseClient<Database>, tareaId: string): Promise<TareaRecordatorio[]> {
    const { data, error } = await client
      .from('tarea_recordatorios')
      .select('*')
      .eq('tarea_id', tareaId)
      .order('fecha_recordatorio')

    if (error) throw error
    return data as TareaRecordatorio[]
  },

  /**
   * Crear recordatorio
   */
  async crearRecordatorio(
    client: SupabaseClient<Database>,
    tareaId: string,
    usuarioId: string,
    datos: RecordatorioFormData
  ): Promise<TareaRecordatorio> {
    // Obtener organizacion_id a través del cliente de la tarea
    const { data: tarea, error: tareaError } = await client
      .from('tareas')
      .select('cliente_id, clientes(organizacion_id)')
      .eq('id', tareaId)
      .single()

    if (tareaError || !tarea) throw tareaError || new Error('Tarea no encontrada')

    // organizacion_id puede ser null si la tarea no tiene cliente
    const organizacionId = (tarea.clientes as { organizacion_id: string } | null)?.organizacion_id || null

    const { data, error } = await client
      .from('tarea_recordatorios')
      .insert({
        tarea_id: tareaId,
        organizacion_id: organizacionId,
        usuario_id: usuarioId,
        fecha_recordatorio: datos.fecha_recordatorio,
        mensaje: datos.mensaje || null,
        tipo: datos.tipo || 'app',
      })
      .select()
      .single()

    if (error) throw error
    return data as TareaRecordatorio
  },

  /**
   * Eliminar recordatorio
   */
  async eliminarRecordatorio(client: SupabaseClient<Database>, id: string): Promise<void> {
    const { error } = await client
      .from('tarea_recordatorios')
      .delete()
      .eq('id', id)

    if (error) throw error
  },

  // ===========================================
  // Delegación de tareas
  // ===========================================

  async asignarTarea(
    client: SupabaseClient<Database>,
    tareaId: string,
    asignadoA: string,
    asignadoPor: string,
    organizacionId: string,
    motivo?: string
  ): Promise<void> {
    const { error } = await client.rpc('asignar_tarea', {
      p_tarea_id: tareaId,
      p_asignado_a: asignadoA,
      p_asignado_por: asignadoPor,
      p_organizacion_id: organizacionId,
      p_motivo: motivo || undefined,
    })
    if (error) throw error
  },

  async obtenerHistorialAsignaciones(client: SupabaseClient<Database>, tareaId: string) {
    const { data, error } = await client.from('tarea_asignaciones')
      .select('*')
      .eq('tarea_id', tareaId)
      .order('created_at', { ascending: false })
    if (error) throw error
    return data as { id: string; organizacion_id: string; tarea_id: string; asignado_a: string; asignado_por: string; motivo: string | null; created_at: string }[]
  },

  async obtenerTareasAsignadasPor(client: SupabaseClient<Database>, email: string, workspace?: string): Promise<TareaCompleta[]> {
    let query = client.from('vista_tareas_completas').select('*').eq('asignado_por', email).neq('estado', 'completada').order('created_at', { ascending: false })
    if (workspace) { query = query.eq('workspace', workspace) }
    const { data, error } = await query
    if (error) throw error
    return data as TareaCompleta[]
  },
}
